
import { useEffect, useState } from 'react'

import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { CompletedOrderStatuses, OrderStatusTypeString } from '@/entities/gridTables/model/OrderStatusType'
import { ordersSelectors } from '@/entities/orders'
import { fetchRunsheetOrders } from '@/entities/orders/thunks/fetchRunsheetOrders'
import { selectRunsheetWithShiftsById, StopTypeEnum } from '@/entities/schedules'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { EntityId } from '@reduxjs/toolkit'

import { OpenOrderRow } from '../models/OpenOrderRow'

export type CloseRunsheetTableData = {
  runsheetName: string
  runsheetDateOfOperation: string
  rows: OpenOrderRow[]
  setRows: React.Dispatch<React.SetStateAction<OpenOrderRow[]>>
  loading: boolean
}

export const useCloseRunsheetTableData = (runsheetId: EntityId): CloseRunsheetTableData => {
  const runsheet = useAppSelector(state => selectRunsheetWithShiftsById(state, runsheetId))
  const shifts = runsheet?.shifts
  const orders = useAppSelector(state => ordersSelectors.selectAll(state))
  const { data: orderStatuses } = useDropdown('orderStatuses')
  const runsheetName = runsheet?.name || ''
  const runsheetDateOfOperation = runsheet?.name || ''

  const [rows, setRows] = useState<OpenOrderRow[]>([])
  const [loading, setLoading] = useState(true)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!runsheet || !shifts) return

    setLoading(true)

    const openOrderRows: OpenOrderRow[] = []
    const ordersToRetrieve: EntityId[] = [] // Orders information to retrieve from the server

    // Get all the unassigned orders
    for (const orderId of runsheet?.unassignedOrders || []) {
      const order = orders.find(ord => ord.id === orderId)

      if (!order) {
        ordersToRetrieve.push(orderId)

        continue
      }

      openOrderRows.push({
        id: orderId,
        shiftId: null,
        isSelectable: true,
        assignedTo: '[Unassigned Orders]',
        order,
        statusType: order.statusType,
        statusTypeString: OrderStatusTypeString[order.statusType],
        statusCellStyle: {},
        newLocation: '** No Change **',
        newLocationCellStyle: {},
        action: 'inbox',
        moveDateOfOperation: null,
      })
    }

    // Iterate through all the stops and get the orders
    for (let i = 0; i < shifts.length; i++) {
      const shift = shifts[i]
      const shiftId = shift.id
      const shiftName = shift.name
      const shiftLoads = shift.loads || []

      for (let j = 0; j < shiftLoads.length; j++) {
        const loadStops = shiftLoads[j]?.stops || []

        for (let k = 0; k < loadStops.length; k++) {
          const stop = loadStops[k]

          if (!stop.isJob || stop.stopType !== StopTypeEnum.Order) continue

          // if this stop's status is in CompletedOrderStatuses, do not process this stop
          if (CompletedOrderStatuses.indexOf(stop.orderStatusType) >= 0) continue

          const order = orders.find(ord => ord.id === stop.orderId)

          const assignedTo = shiftName
          const statusType = stop.orderStatusType
          const statusTypeString = OrderStatusTypeString[stop.orderStatusType]

          // Updating the order status cell background color
          const orderStatus = orderStatuses?.find(status => status.value === stop.orderStatusType)
          const statusCellStyle = {
            backgroundColor: orderStatus?.backgroundColor || null,
            color: orderStatus?.fontColor || null,
          }

          if (!order) {
            ordersToRetrieve.push(stop.orderId)

            continue
          }

          openOrderRows.push({
            id: stop.orderId,
            isSelectable: true,
            shiftId: shiftId,
            assignedTo: assignedTo,
            order: order,
            statusType: statusType,
            statusTypeString: statusTypeString,

            statusCellStyle: statusCellStyle,
            newLocation: '** No Change **',
            newLocationCellStyle: {},
            action: '',
            moveDateOfOperation: null,
          })
        }
      }
    }

    if (ordersToRetrieve.length > 0) {
      dispatch(fetchRunsheetOrders({ orderIds: ordersToRetrieve }))
        .then(() => {
          setLoading(false)
        })
    }
    else {
      setRows(openOrderRows)
      setLoading(false)
    }
  }, [runsheet, shifts, orders, orderStatuses, dispatch])

  return {
    runsheetName,
    runsheetDateOfOperation,
    loading: loading,
    rows: rows,
    setRows: setRows,
  }
}